import type { PersonalReportSummaryJson } from '../../data/mockPersonalReportSummary';

export type EvalRow = NonNullable<PersonalReportSummaryJson['evaluationBreakdown']>[number];

const NINEBOX_LABELS: Record<string, string> = {
  HH: 'HH明星人才',
  HM: 'HM潜力人才',
  HL: 'HL潜力待兑现',
  MH: 'MH中坚骨干',
  MM: 'MM稳定贡献',
  ML: 'ML待观察',
  LH: 'LH可靠执行',
  LM: 'LM待改进',
  LL: 'LL亟需改进',
};

export function nineBoxLabel(cell: unknown): string | null {
  if (cell == null || cell === '') return null;
  const key = String(cell).toUpperCase();
  return NINEBOX_LABELS[key] ?? null;
}

export function formatTenure(hireDate?: string | null): string | null {
  if (!hireDate) return null;
  const start = new Date(hireDate);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  let months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) return null;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  if (years === 0) return `${remMonths} 个月`;
  if (remMonths === 0) return `${years} 年`;
  return `${years} 年 ${remMonths} 个月`;
}

export function competencyBand(score: number): string {
  if (score >= 4.5) return '优秀（4.5+）';
  if (score >= 4.0) return '良好（4.0~4.5）';
  if (score >= 3.5) return '合格（3.5~4.0）';
  return '待加强（<3.5）';
}

export function competencyMeetsRequirement(score: number | null | undefined): boolean | null {
  if (score == null || Number.isNaN(Number(score))) return null;
  return Number(score) >= 3.5;
}

export function isHighPotential(label: unknown): boolean | null {
  if (label == null || label === '') return null;
  const text = String(label);
  // 排除词须先于「高潜」子串匹配，否则「非高潜」会因含「高潜」被误判为 true
  if (/非高潜|非加速|不纳入|暂不|观察池/.test(text)) return false;
  if (/高潜|加速|保持/.test(text)) return true;
  return null;
}

export type CoreConclusion = '具备培养价值' | '稳定使用' | '需改进观察';

/** 九宫格格位 × 高潜识别 → 核心结论（规则判定表） */
export function coreConclusion(
  finalCell: unknown,
  hpLabel: unknown,
): CoreConclusion | null {
  if (finalCell == null || finalCell === '') return null;
  const cell = String(finalCell).toUpperCase();
  const hp = isHighPotential(hpLabel);

  switch (cell) {
    case 'HH':
      if (hp === false) return '稳定使用';
      return '具备培养价值';
    case 'HM':
    case 'MH':
      if (hp === true) return '具备培养价值';
      return '稳定使用';
    case 'HL':
      if (hp === true) return '具备培养价值';
      if (hp === false) return '需改进观察';
      return '稳定使用';
    case 'MM':
    case 'LH':
      return '稳定使用';
    case 'ML':
    case 'LM':
    case 'LL':
      return '需改进观察';
    default:
      return null;
  }
}

const INSIGHT_FILLER_PREFIX =
  /^(?:建议|需要|该员工|应当|可以|请|应|宜|需|其|该人|本人|员工)/;

/** 去掉 AI 洞察条目前导填充词 */
export function stripInsightFiller(text: string): string {
  let s = text.trim();
  let prev = '';
  while (s !== prev) {
    prev = s;
    s = s.replace(INSIGHT_FILLER_PREFIX, '').trim();
  }
  return s;
}

const NEGATION_CHARS = /[不无未]/;

/**
 * 将已按分句拆好的短语压缩为 ~12–14 字展示文案；仅在子句边界（，、；）截断，
 * 避免切断否定词。完整原文由调用方放入 title tooltip。
 */
export function condenseInsightPhrase(
  text: string,
  maxChars = 14,
): { short: string; full: string } {
  const full = text.trim();
  const stripped = stripInsightFiller(full);
  if (!stripped) return { short: full, full };
  if ([...stripped].length <= maxChars) return { short: stripped, full };

  const subClauses = stripped.split(/(?<=[，,、；;])\s*/).filter(Boolean);
  if (subClauses.length > 1) {
    let acc = '';
    for (const part of subClauses) {
      const candidate = acc ? `${acc}${part}` : part;
      if ([...candidate].length <= maxChars) {
        acc = candidate;
      } else {
        break;
      }
    }
    if (acc) return { short: acc.trim(), full };
  }

  const chars = [...stripped];
  let cut = Math.min(maxChars, chars.length);
  while (cut > 4 && cut < chars.length) {
    const before = chars[cut - 1];
    const after = chars[cut];
    if (NEGATION_CHARS.test(after) && !/[，,、；;。\s]/.test(before)) {
      cut -= 1;
      continue;
    }
    if (NEGATION_CHARS.test(before)) {
      cut += 1;
      if (cut > maxChars + 3) break;
      continue;
    }
    break;
  }
  if (cut >= chars.length) return { short: stripped, full };
  return { short: chars.slice(0, cut).join('').trim(), full };
}

export function mergeEvalDimNames(
  rows: NonNullable<PersonalReportSummaryJson['evaluationBreakdown']>,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    for (const d of r.dimensions) {
      if (!seen.has(d.dimName)) {
        seen.add(d.dimName);
        out.push(d.dimName);
      }
    }
  }
  return out;
}

export function scoreEffectiveDim(
  row: EvalRow,
  er: { label?: string | null; code?: string | null },
): number | null {
  const label = er.label ?? '';
  const code = er.code ?? '';
  const hit = row.dimensions.find(
    (d) =>
      (label !== '' && d.dimName === label) ||
      (code !== '' && (String(d.dimCode ?? '') === code || d.dimName === code)),
  );
  return hit != null ? hit.score : null;
}

export function splitProfile(profile: string): { headline: string; body: string } {
  const text = profile.trim();
  const match = text.match(/^(.+?[。！？!?])(?:\s*)([\s\S]+)$/);
  return match
    ? { headline: match[1].trim(), body: match[2].trim() }
    : { headline: text, body: '' };
}

export function splitNumberedActions(suggestions: string): string[] {
  const text = suggestions.trim();
  const parts = text
    .split(/(?:^|\r?\n)\s*(?:\d+[.、)）]|[①②③④⑤⑥⑦⑧⑨⑩])\s*/)
    .map((line) => line.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts : [text];
}

export function parseAiInsight(profile: string): {
  summary: string;
  advantages: string[];
  risks: string[];
} {
  const { headline, body } = splitProfile(profile);
  const summary = headline;

  const advSection = body.match(/(?:优势|强项)[：:]\s*([\s\S]*?)(?=(?:风险|短板|关注)[：:]|$)/);
  const riskSection = body.match(/(?:风险|短板|关注)[：:]\s*([\s\S]*?)$/);

  if (advSection || riskSection) {
    const splitItems = (raw: string) =>
      raw
        .split(/[；;。\n]/)
        .map((s) => s.replace(/^[\s\-·•\d.、)）]+/, '').trim())
        .filter((s) => s.length > 4);
    return {
      summary,
      advantages: advSection ? splitItems(advSection[1]) : [],
      risks: riskSection ? splitItems(riskSection[1]) : [],
    };
  }

  if (!body) return { summary, advantages: [], risks: [] };

  const sentences = body
    .split(/[。！？!?]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 4);

  const advantages: string[] = [];
  const risks: string[] = [];
  for (const s of sentences) {
    if (/优势|强项|亮点|口碑|稳定|在位|正向|认可|成熟|清晰/.test(s)) {
      advantages.push(s.endsWith('。') ? s : `${s}。`);
    } else if (/风险|短板|关注|审慎|分歧|阻力|偏低|盲区|偏差|保守|噪声|落差/.test(s)) {
      risks.push(s.endsWith('。') ? s : `${s}。`);
    }
  }

  return { summary, advantages, risks };
}

export function computeEffectiveRadar(
  radar: PersonalReportSummaryJson['radar'],
  evaluationBreakdown: PersonalReportSummaryJson['evaluationBreakdown'],
): PersonalReportSummaryJson['radar'] {
  if ((radar?.length ?? 0) > 0) return radar;
  const breakdown = evaluationBreakdown ?? [];
  const dimNames = mergeEvalDimNames(breakdown);
  if (!dimNames.length) return [];
  return dimNames.map((dimName) => {
    const vals: number[] = [];
    let dimCode: string | undefined;
    for (const row of breakdown) {
      const hit = row.dimensions.find((d) => d.dimName === dimName);
      if (hit) {
        vals.push(hit.score);
        const c = hit.dimCode;
        if (c != null && c !== '') dimCode = String(c);
      }
    }
    const avg =
      vals.length === 0
        ? 0
        : Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
    return { code: dimCode, label: dimName, avg };
  });
}

export function radarAdvantagesConcerns(
  effectiveRadar: PersonalReportSummaryJson['radar'],
  qualityAnalysisMeta: PersonalReportSummaryJson['qualityAnalysisMeta'],
): { advantages: PersonalReportSummaryJson['radar']; concerns: PersonalReportSummaryJson['radar'] } {
  const advThreshold = qualityAnalysisMeta?.advantageMeanThreshold ?? 5.5;
  const concernThreshold = qualityAnalysisMeta?.concernMeanThreshold ?? 4;
  return {
    advantages: effectiveRadar.filter((item) => item.avg >= advThreshold),
    concerns: effectiveRadar.filter((item) => item.avg < concernThreshold),
  };
}

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function consistencyFromRange(range: number, fullScore: number): number {
  if (fullScore <= 0) return 0;
  return clamp(100 - (range / fullScore) * 100, 0, 100);
}

export function consistencyTier(range: number): {
  label: string;
  tone: 'good' | 'mid' | 'bad';
} {
  if (range < 5) return { label: '高度一致', tone: 'good' };
  if (range <= 15) return { label: '基本一致', tone: 'mid' };
  return { label: '分歧明显', tone: 'bad' };
}
